import React, { createContext, useContext, useState, useEffect } from 'react';
import { logger } from '@/services/LoggingService';
import { NavigationState, VezgoNavigationContextType, ReturnOptions, defaultNavigationState } from './types';
import { vezgoConfig } from '@/config';
import { migrateLegacyData } from './migration';
import { VEZGO_RETURN_PATH } from '../../constants';
import { toast } from 'sonner';
import { VezgoStorageKeys } from '../../utils/storage-keys';

const NavigationContext = createContext<VezgoNavigationContextType>({
  navigationState: defaultNavigationState,
  setNavigationState: () => {},
  saveCurrentLocation: () => {},
  returnToPreviousLocation: () => {},
  redirectToInvestments: () => {},
  isReturningFromConnection: false,
  saveState: () => {},
  getState: () => null,
  saveProvider: () => {},
  getProvider: () => null,
  detectReloadLoop: () => false,
  clearAllNavigationData: () => {},
  saveToStorage: () => false,
  getFromStorage: () => null
});

export const useVezgoNavigation = () => useContext(NavigationContext);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [navigationState, setNavigationStateInternal] = useState<NavigationState>(defaultNavigationState);
  const [isReturningFromConnection, setIsReturningFromConnection] = useState(false);

  useEffect(() => {
    logger.debug('NavigationProvider', 'Running migration from sessionStorage');
    const migrationResults = migrateLegacyData();
    logger.debug('NavigationProvider', 'Migration completed', migrationResults);

    const storedProvider = localStorage.getItem(vezgoConfig.providerStorageKey);
    const storedState = localStorage.getItem(vezgoConfig.stateStorageKey);
    const storedReturnUrl = localStorage.getItem(vezgoConfig.returnUrlStorageKey);

    setNavigationStateInternal(prev => ({
      ...prev,
      provider: storedProvider || prev.provider,
      returnUrl: storedReturnUrl || null
    }));

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('state') && storedState && urlParams.get('state') === storedState) {
      setIsReturningFromConnection(true);
    }
  }, []);

  const setNavigationState = (partialState: Partial<NavigationState>) => {
    setNavigationStateInternal(prev => ({
      ...prev,
      ...partialState,
      timestamp: Date.now()
    }));

    if (partialState.provider) {
      localStorage.setItem(vezgoConfig.providerStorageKey, partialState.provider);
    }
    
    if (partialState.returnUrl !== undefined) {
      if (partialState.returnUrl) {
        localStorage.setItem(vezgoConfig.returnUrlStorageKey, partialState.returnUrl);
      } else {
        localStorage.removeItem(vezgoConfig.returnUrlStorageKey);
      }
    }
  };

  const saveProvider = (provider: string) => {
    localStorage.setItem(vezgoConfig.providerStorageKey, provider);
    setNavigationState({ provider });
  };

  const getProvider = () => localStorage.getItem(vezgoConfig.providerStorageKey) || navigationState.provider || null;

  const saveState = (state: string) => {
    localStorage.setItem(vezgoConfig.stateStorageKey, state);
  };

  const getState = () => localStorage.getItem(vezgoConfig.stateStorageKey);

  const saveCurrentLocation = (provider?: string, source?: string) => {
    const currentUrl = window.location.href;
    if (
      window.location.pathname.includes('/investments/connect') ||
      window.location.pathname.endsWith('/investments')
    ) {
      logger.info('NavigationProvider', '[LOOP PROTECTION] Not saving returnUrl because on connect/investments page', { currentUrl });
      return;
    }
    localStorage.setItem(vezgoConfig.returnUrlStorageKey, currentUrl);
    setNavigationState({
      returnUrl: currentUrl,
      returnSource: source || 'manual',
      preserveState: true
    });

    if (provider) {
      saveProvider(provider);
    }
  };

  const clearAllNavigationData = () => {
    localStorage.removeItem(vezgoConfig.stateStorageKey);
    localStorage.removeItem(vezgoConfig.returnUrlStorageKey);
    localStorage.removeItem(vezgoConfig.providerStorageKey);

    localStorage.removeItem(VezgoStorageKeys.CALLBACK_PROCESSED);
    localStorage.removeItem(VezgoStorageKeys.CALLBACK_TIME);
    localStorage.removeItem(VezgoStorageKeys.CALLBACK_PROVIDER);
    localStorage.removeItem(VezgoStorageKeys.CALLBACK_CONNECTION_ID);

    sessionStorage.removeItem('reload_count');
    setNavigationStateInternal(defaultNavigationState);
  };

  const detectReloadLoop = () => {
    const reloadCount = parseInt(sessionStorage.getItem('reload_count') || '0', 10);
    if (reloadCount > 3) {
      return true;
    }
    sessionStorage.setItem('reload_count', (reloadCount + 1).toString());
    return false;
  };

  const saveToStorage = (key: string, value: string): boolean => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      logger.error('NavigationProvider', 'Error saving to storage', {
        error: error instanceof Error ? error.message : String(error),
        key
      });
      return false;
    }
  };

  const getFromStorage = (key: string, defaultValue: string | null = null): string | null => {
    try {
      const value = localStorage.getItem(key);
      return value || defaultValue;
    } catch (error) {
      logger.error('NavigationProvider', 'Storage access failed', {
        key,
        error: error instanceof Error ? error.message : String(error)
      });
      return defaultValue;
    }
  };

  const returnToPreviousLocation = (options?: ReturnOptions) => {
    const storedReturnUrl = localStorage.getItem(vezgoConfig.returnUrlStorageKey);
    const returnUrl = storedReturnUrl || navigationState.returnUrl;
    
    logger.info('NavigationProvider', '[REDIRECT] Initiating returnToPreviousLocation', {
      providedReturnUrl: returnUrl,
      options,
      contextReturnSource: navigationState.returnSource,
      storageFlags: {
        state: localStorage.getItem(vezgoConfig.stateStorageKey),
        provider: localStorage.getItem(vezgoConfig.providerStorageKey),
        returnUrl: localStorage.getItem(vezgoConfig.returnUrlStorageKey),
        callbackProcessed: localStorage.getItem(VezgoStorageKeys.CALLBACK_PROCESSED),
        callbackTime: localStorage.getItem(VezgoStorageKeys.CALLBACK_TIME),
        callbackProvider: localStorage.getItem(VezgoStorageKeys.CALLBACK_PROVIDER),
        callbackConnectionId: localStorage.getItem(VezgoStorageKeys.CALLBACK_CONNECTION_ID),
      },
      navigationState
    });

    if (returnUrl) {
      clearAllNavigationData();

      const returnLocation = new URL(returnUrl);
      if (options?.additionalParams) {
        Object.entries(options.additionalParams).forEach(([key, value]) => {
          returnLocation.searchParams.set(key, value);
        });
      }

      if (options?.showToast && options?.toastMessage) {
        toast.success(options.toastMessage);
      }

      const delay = options?.delay || 0;
      setTimeout(() => {
        logger.info('NavigationProvider', '[REDIRECT] Executing navigation to stored returnUrl', {
          returnUrl: returnLocation.toString(),
          replaceFlag: options?.replace
        });
        sessionStorage.removeItem('reload_count');
        if (options?.replace) {
          window.location.replace(returnLocation.toString());
        } else {
          window.location.href = returnLocation.toString();
        }
      }, delay);
    } else {
      logger.warn('NavigationProvider', '[REDIRECT] No return URL found for navigation', {
        allStorageFlags: {
          state: localStorage.getItem(vezgoConfig.stateStorageKey),
          provider: localStorage.getItem(vezgoConfig.providerStorageKey),
          returnUrl: localStorage.getItem(vezgoConfig.returnUrlStorageKey),
        }
      });
    }
  };

  const redirectToInvestments = (message?: string) => {
    const targetPath = `${VEZGO_RETURN_PATH}?timestamp=${Date.now()}`;
    logger.info('NavigationProvider', '[REDIRECT] Redirecting to investments', {
      destination: targetPath,
      storageFlags: {
        state: localStorage.getItem(vezgoConfig.stateStorageKey),
        provider: localStorage.getItem(vezgoConfig.providerStorageKey),
        returnUrl: localStorage.getItem(vezgoConfig.returnUrlStorageKey),
        callbackProcessed: localStorage.getItem(VezgoStorageKeys.CALLBACK_PROCESSED),
        callbackTime: localStorage.getItem(VezgoStorageKeys.CALLBACK_TIME),
        callbackProvider: localStorage.getItem(VezgoStorageKeys.CALLBACK_PROVIDER),
        callbackConnectionId: localStorage.getItem(VezgoStorageKeys.CALLBACK_CONNECTION_ID),
      }
    });
    if (message) {
      toast.success(message);
    }
    window.location.href = targetPath;
  };

  return (
    <NavigationContext.Provider
      value={{
        navigationState,
        setNavigationState,
        saveCurrentLocation,
        returnToPreviousLocation,
        redirectToInvestments,
        isReturningFromConnection,
        saveState,
        getState,
        saveProvider,
        getProvider,
        detectReloadLoop,
        clearAllNavigationData,
        saveToStorage,
        getFromStorage
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};
